import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { Upload as UploadIcon } from '@opengov/react-capital-assets';
import React, { useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import locale from '../customReports-locale.json';
import { DEFAULT_ACCEPTED_FILE_TYPES, DEFAULT_MAX_SIZE } from '../CustomReports.constants';
import { determineErrorMessage } from '../CustomReports.utils';
import { FontAccessibilityWarning } from '../FontAccessibilityWarning';
import { SelectedFile } from './SelectedFile';
import { styles } from './UploadStep.styles';

type Props = {
  onFileSelected: (files: File[]) => void;
};

type UploadState =
  | {
      variant: 'idle';
    }
  | {
      variant: 'error';
      fileRejection: FileRejection;
    };

export function UploadStep({ onFileSelected }: Props) {
  const [uploadState, setUploadState] = useState<UploadState>({ variant: 'idle' });
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: DEFAULT_ACCEPTED_FILE_TYPES,
    noClick: true,
    maxSize: DEFAULT_MAX_SIZE,
    multiple: false,
    onDrop: (acceptedFiles) => {
      onFileSelected(acceptedFiles);
    },
    onDropRejected: (fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        setUploadState({ variant: 'error', fileRejection: fileRejections[0] });
      }
    }
  });

  return (
    <Box sx={styles.container} data-test="new-excel-file-upload-step">
      {uploadState.variant === 'idle' ? (
        <>
          <Typography sx={styles.title}>{locale.addNewExcelFileModal.chooseFilesToUpload}</Typography>
          <Box {...getRootProps()} sx={styles.dropzoneDefault}>
            <input {...getInputProps()} />
            <Box
              sx={isDragActive ? styles.dropzoneActive : styles.dropzoneInactive}
              data-test="new-excel-file-dropzone"
            >
              <Box sx={styles.insideContainer}>
                <SvgIcon component={UploadIcon} inheritViewBox sx={styles.uploadIcon} />
                <Typography variant="body1" sx={styles.dropzoneText}>
                  {locale.addNewExcelFileModal.dragFileHere}{' '}
                  <Button
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      open();
                    }}
                    sx={styles.selectFilesButton}
                    disableRipple={true}
                    data-test="upload-step-select-files-button"
                  >
                    {locale.addNewExcelFileModal.clickHereToSelectFiles}
                  </Button>
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={styles.fontAccessibilityWarningContainer}>
            <FontAccessibilityWarning />
          </Box>
        </>
      ) : null}

      {uploadState.variant === 'error' ? (
        <SelectedFile
          fileName={uploadState.fileRejection.file.name}
          onReUpload={() => setUploadState({ variant: 'idle' })}
          errors={determineErrorMessage(uploadState.fileRejection.errors)}
        />
      ) : null}
    </Box>
  );
}
